import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import DoNotDisturbAltIcon from "@mui/icons-material/DoNotDisturbAlt";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import SearchIcon from "@mui/icons-material/Search";
import StorefrontIcon from "@mui/icons-material/Storefront";

const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;

const authHeaders = () => {
  const token = localStorage.getItem("accessToken");
  return token ? { accessToken: token } : {};
};

const emptyConnectionForm = {
  id: "",
  provider: "woocommerce",
  name: "WooCommerce",
  baseUrl: "",
  consumerKey: "",
  consumerSecret: "",
};

const sourceOptions = [
  { value: "all", label: "All sources" },
  { value: "catalog", label: "PromoWorld" },
  { value: "custom", label: "Custom products" },
  { value: "asi", label: "ASI" },
];

export const COMMERCE_IMPORT_PRODUCT_PAGE_SIZE = 50;

const getProducts = (importRecord) => (Array.isArray(importRecord?.products) ? importRecord.products : []);

export const getCommerceImportProductPaginationMeta = (products = [], page = 0, pageSize = COMMERCE_IMPORT_PRODUCT_PAGE_SIZE) => {
  const total = Array.isArray(products) ? products.length : 0;
  const safePageSize = Math.max(Number(pageSize) || COMMERCE_IMPORT_PRODUCT_PAGE_SIZE, 1);
  const pageCount = Math.max(Math.ceil(total / safePageSize), 1);
  const currentPage = Math.min(Math.max(Number(page) || 0, 0), pageCount - 1);
  const startIndex = total ? currentPage * safePageSize : 0;
  const endIndex = Math.min(startIndex + safePageSize, total);
  return { total, pageSize: safePageSize, pageCount, currentPage, startIndex, endIndex };
};

export const getCommerceImportProductPage = (products = [], page = 0, pageSize = COMMERCE_IMPORT_PRODUCT_PAGE_SIZE) => {
  const rows = Array.isArray(products) ? products : [];
  const meta = getCommerceImportProductPaginationMeta(rows, page, pageSize);
  return rows.slice(meta.startIndex, meta.endIndex);
};

const countByStatus = (products) =>
  products.reduce((acc, product) => {
    acc[product.mappingStatus] = (acc[product.mappingStatus] || 0) + 1;
    return acc;
  }, {});

const money = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? `$${parsed.toFixed(2)}` : "No price";
};

const imageFromProduct = (snapshot) => {
  const images = Array.isArray(snapshot?.images) ? snapshot.images : [];
  return images[0]?.src || snapshot?.imageUrl || "";
};

const summarizeColorsSizes = (snapshot) => {
  const variants = Array.isArray(snapshot?.variants) ? snapshot.variants : [];
  const colors = [...new Set(variants.map((variant) => variant?.color).filter(Boolean))];
  const sizes = [...new Set(variants.map((variant) => variant?.size).filter(Boolean))];
  return [
    colors.length ? `${colors.slice(0, 4).join(", ")}${colors.length > 4 ? " +" : ""}` : "",
    sizes.length ? `${sizes.slice(0, 4).join(", ")}${sizes.length > 4 ? " +" : ""}` : "",
  ].filter(Boolean).join(" / ");
};

const getSuggestedMatch = (product) => product?.mappingOverrides?.suggestedMatch || null;
const getSelectedMatch = (product) => product?.mappingOverrides?.selectedMatch || null;

const cleanText = (value) => (value === undefined || value === null ? "" : String(value).trim());

const getVariantSku = (variant = {}) =>
  cleanText(variant.sku ?? variant.SKU ?? variant.Number ?? variant.number ?? variant.ExternalVariantId ?? variant.externalVariantId);

const getVariantColor = (variant = {}) => cleanText(variant.color ?? variant.Color ?? variant.Colour ?? variant.colour);
const getVariantSize = (variant = {}) => cleanText(variant.size ?? variant.Size);

const getWooVariantKey = (variant = {}) =>
  cleanText(variant.externalId ?? variant.id ?? variant.sku ?? variant.SKU);

const getSourceVariantId = (variant = {}) =>
  cleanText(variant.id ?? variant.Id ?? variant.ExternalVariantId ?? variant.externalVariantId ?? getVariantSku(variant));

const getVariantCost = (variant = {}) => {
  const direct = Number(variant.Cost ?? variant.cost ?? variant.NetCost ?? variant.netCost ?? variant.UnitCost ?? variant.unitCost);
  if (Number.isFinite(direct)) return direct;
  const prices = Array.isArray(variant.Prices || variant.prices) ? (variant.Prices || variant.prices) : [];
  const price = prices.find((row) => Number.isFinite(Number(row?.Cost ?? row?.cost ?? row?.NetCost ?? row?.netCost)));
  return price ? Number(price.Cost ?? price.cost ?? price.NetCost ?? price.netCost) : null;
};

const formatVariantParts = (variant = {}, { includeRetail = false, includeCost = false } = {}) => {
  const parts = [
    getVariantSku(variant) || "No SKU",
    getVariantColor(variant) || "No color",
    getVariantSize(variant) || "No size",
  ];
  if (includeRetail) parts.push(money(variant.retail ?? variant.price ?? variant.Price ?? variant.Retail));
  if (includeCost) {
    const cost = getVariantCost(variant);
    parts.push(cost == null ? "No cost" : money(cost));
  }
  return parts.join(" · ");
};

const getSourceVariantsFromSelectedMatch = (selected) => {
  const metadata = selected?.metadata || {};
  const raw = metadata.raw || metadata;
  const variants = raw.Variants || raw.variants || metadata.variants || [];
  if (Array.isArray(variants) && variants.length) return variants;
  const skuRows = raw.Sku || raw.SKU || [];
  return Array.isArray(skuRows) ? skuRows : [];
};

function VariantMappingPanel({ product, selected, disabled, onSave }) {
  const snapshot = product?.externalSnapshot || {};
  const wooVariants = Array.isArray(snapshot.variants) && snapshot.variants.length
    ? snapshot.variants
    : [{ externalId: snapshot.externalId, sku: snapshot.sku, retail: snapshot.retail }];
  const sourceVariants = getSourceVariantsFromSelectedMatch(selected);
  const variantMappings = product?.mappingOverrides?.variantMappings || {};
  const sourceType = selected?.sourceType || product?.matchedSourceType;
  const productId = selected?.productId || product?.matchedProductId;

  if (!selected || !wooVariants.length || !sourceVariants.length || !sourceType || !productId) return null;

  const handleVariantMappingChange = (wooVariant) => (event) => {
    const key = getWooVariantKey(wooVariant);
    if (!key) return;
    const sourceVariantId = event.target.value;
    const nextMappings = { ...variantMappings };
    if (!sourceVariantId) {
      delete nextMappings[key];
    } else {
      const matchedVariant = sourceVariants.find((variant) => getSourceVariantId(variant) === sourceVariantId) || {};
      nextMappings[key] = {
        externalVariantId: cleanText(wooVariant.externalId ?? wooVariant.id ?? key),
        sku: getVariantSku(wooVariant),
        sourceVariantId,
        sourceSku: getVariantSku(matchedVariant),
      };
    }
    onSave(product, {
      sourceType,
      productId,
      metadata: selected.metadata || null,
      variantMappings: nextMappings,
    });
  };

  return (
    <Box sx={{ border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 1, p: 1 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={700}>
        Manual variant mapping
      </Typography>
      <Stack spacing={1} sx={{ mt: 1 }}>
        {wooVariants.map((wooVariant, index) => {
          const key = getWooVariantKey(wooVariant) || `variant-${index}`;
          const mapped = variantMappings[key] || {};
          return (
            <Grid container spacing={1} alignItems="center" key={key}>
              <Grid item xs={12} md={5}>
                <Typography variant="body2">{formatVariantParts(wooVariant, { includeRetail: true })}</Typography>
              </Grid>
              <Grid item xs={12} md={7}>
                <FormControl size="small" fullWidth disabled={disabled}>
                  <InputLabel>Elite variation</InputLabel>
                  <Select
                    label="Elite variation"
                    value={mapped.sourceVariantId || ""}
                    onChange={handleVariantMappingChange(wooVariant)}
                  >
                    <MenuItem value="">No explicit mapping</MenuItem>
                    {sourceVariants.map((sourceVariant, sourceIndex) => {
                      const value = getSourceVariantId(sourceVariant) || `source-${sourceIndex}`;
                      return (
                        <MenuItem key={value} value={value}>
                          {formatVariantParts(sourceVariant, { includeCost: true })}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          );
        })}
      </Stack>
    </Box>
  );
}

function ProductSearchPanel({
  product,
  importRecord,
  promoStoreId,
  instanceUniqueId,
  disabled,
  onSelect,
}) {
  const snapshot = product?.externalSnapshot || {};
  const [sourceType, setSourceType] = useState("all");
  const [asiMarket, setAsiMarket] = useState("canada");
  const [query, setQuery] = useState(snapshot.sku || snapshot.name || "");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setQuery(snapshot.sku || snapshot.name || "");
    setResults([]);
    setError("");
  }, [product?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const search = async () => {
    if (!importRecord?.id || !query.trim()) return;
    setSearching(true);
    setError("");
    try {
      const { data } = await axios.get(`${SERVER}/promostores/${promoStoreId}/imports/${importRecord.id}/matching/search`, {
        headers: authHeaders(),
        params: {
          instanceUniqueId,
          q: query,
          sourceType,
          includeAsi: sourceType === "all",
          marketScope: asiMarket,
        },
      });
      setResults(Array.isArray(data?.results) ? data.results : []);
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || err?.message || "Search failed.");
    } finally {
      setSearching(false);
    }
  };

  return (
    <Stack spacing={1}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Source</InputLabel>
          <Select
            label="Source"
            value={sourceType}
            onChange={(event) => {
              setSourceType(event.target.value);
              setResults([]);
              setError("");
            }}
          >
            {sourceOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {(sourceType === "asi" || sourceType === "all") && (
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>ASI market</InputLabel>
            <Select
              label="ASI market"
              value={asiMarket}
              onChange={(event) => {
                setAsiMarket(event.target.value);
                setResults([]);
                setError("");
              }}
            >
              <MenuItem value="canada">CAN</MenuItem>
              <MenuItem value="global">ALL</MenuItem>
            </Select>
          </FormControl>
        )}
        <TextField
          size="small"
          label="Find matching Elite Core product"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          fullWidth
          onKeyDown={(event) => {
            if (event.key === "Enter") search();
          }}
        />
        <Button variant="outlined" startIcon={<SearchIcon />} onClick={search} disabled={disabled || searching || !query.trim()}>
          Search
        </Button>
      </Stack>
      {searching && <LinearProgress />}
      {error && <Alert severity="error">{error}</Alert>}
      <Stack spacing={1}>
        {results.slice(0, 8).map((result) => (
          <Paper key={`${result.sourceType}:${result.productId}`} variant="outlined" sx={{ p: 1, borderRadius: 1 }}>
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Avatar variant="rounded" src={result.imageUrl || undefined} sx={{ width: 42, height: 42 }}>
                {result.sourceLabel?.charAt(0) || "P"}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={700} noWrap>{result.name || "Untitled product"}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {result.sourceLabel || result.sourceType} · {result.supplierName || "Unknown supplier"} · {result.sku || result.productId}
                </Typography>
              </Box>
              <Button size="small" variant="contained" color="error" onClick={() => onSelect(result)} disabled={disabled}>
                Select
              </Button>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Stack>
  );
}

function PromoStoreCommerceImportDialog({
  open,
  onClose,
  promoStoreId,
  instanceUniqueId,
  onImported,
}) {
  const [connections, setConnections] = useState([]);
  const [connectionForm, setConnectionForm] = useState(emptyConnectionForm);
  const [importRecord, setImportRecord] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [productPage, setProductPage] = useState(0);

  const products = useMemo(() => getProducts(importRecord), [importRecord]);
  const statusCounts = useMemo(() => countByStatus(products), [products]);
  const productPagination = useMemo(
    () => getCommerceImportProductPaginationMeta(products, productPage),
    [productPage, products],
  );
  const visibleProducts = useMemo(
    () => getCommerceImportProductPage(products, productPagination.currentPage, productPagination.pageSize),
    [productPagination.currentPage, productPagination.pageSize, products],
  );
  const canUseImport = Boolean(promoStoreId && instanceUniqueId);

  useEffect(() => {
    setProductPage(0);
  }, [importRecord?.id]);

  useEffect(() => {
    if (productPage !== productPagination.currentPage) {
      setProductPage(productPagination.currentPage);
    }
  }, [productPage, productPagination.currentPage]);

  const requestUrl = useCallback(
    (path) => `${SERVER}/promostores/${promoStoreId}${path}`,
    [promoStoreId],
  );

  const loadConnections = useCallback(async () => {
    if (!open || !canUseImport) return;
    setError("");
    const { data } = await axios.get(requestUrl("/import/connections"), {
      headers: authHeaders(),
      params: { instanceUniqueId },
    });
    const rows = Array.isArray(data?.connections) ? data.connections : [];
    setConnections(rows);
    setImportRecord(null);
    if (rows[0]) {
      setConnectionForm({
        ...emptyConnectionForm,
        id: rows[0].id,
        provider: rows[0].provider || "woocommerce",
        name: rows[0].name || "WooCommerce",
        baseUrl: rows[0].baseUrl || "",
      });
    } else {
      setConnectionForm({ ...emptyConnectionForm });
    }
  }, [canUseImport, instanceUniqueId, open, promoStoreId, requestUrl]);

  useEffect(() => {
    loadConnections().catch((err) => setError(err?.response?.data?.message || err?.message || "Failed to load commerce connections."));
  }, [loadConnections]);

  const handleFieldChange = (field) => (event) => {
    setConnectionForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const saveConnection = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(requestUrl("/import/connections"), {
        id: connectionForm.id || undefined,
        instanceUniqueId,
        provider: connectionForm.provider,
        name: connectionForm.name,
        baseUrl: connectionForm.baseUrl,
        credentials: {
          consumerKey: connectionForm.consumerKey,
          consumerSecret: connectionForm.consumerSecret,
        },
        testConnection: true,
      }, {
        headers: authHeaders(),
      });
      const saved = data?.connection;
      setConnections((prev) => [saved, ...prev.filter((row) => row.id !== saved.id)]);
      setConnectionForm((prev) => ({ ...prev, id: saved.id, consumerKey: "", consumerSecret: "" }));
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to save commerce connection.");
    } finally {
      setBusy(false);
    }
  };

  const scanProducts = async () => {
    setBusy(true);
    setError("");
    try {
      const connectionId = connectionForm.id || connections[0]?.id;
      const created = await axios.post(requestUrl("/imports"), {
        instanceUniqueId,
        connectionId,
      }, {
        headers: authHeaders(),
      });
      const importId = created.data?.import?.id;
      const scanned = await axios.post(requestUrl(`/imports/${importId}/scan`), {
        instanceUniqueId,
      }, {
        headers: authHeaders(),
      });
      setImportRecord(scanned.data?.import || null);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to scan products.");
    } finally {
      setBusy(false);
    }
  };

  const saveMapping = async (product, payload) => {
    if (!importRecord?.id || !product?.id) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.put(requestUrl(`/imports/${importRecord.id}/products/${product.id}/mapping`), {
        instanceUniqueId,
        ...payload,
      }, {
        headers: authHeaders(),
      });
      setImportRecord(data?.import || null);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to update product mapping.");
    } finally {
      setBusy(false);
    }
  };

  const importProducts = async () => {
    if (!importRecord?.id) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(requestUrl(`/imports/${importRecord.id}/import`), {
        instanceUniqueId,
      }, {
        headers: authHeaders(),
      });
      setImportRecord(data?.import || null);
      await onImported?.();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to import products.");
    } finally {
      setBusy(false);
    }
  };

  const importableProducts = products.filter((product) => ["mapped", "failed"].includes(product.mappingStatus));

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <StorefrontIcon fontSize="small" />
        Import Store Products
      </DialogTitle>
      <DialogContent dividers>
        {busy && <LinearProgress sx={{ mb: 2 }} />}
        {!canUseImport && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            PromoStore and instance context are required before importing.
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack spacing={2}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
            <TextField select size="small" label="Provider" value={connectionForm.provider} onChange={handleFieldChange("provider")} sx={{ minWidth: 170 }}>
              <MenuItem value="woocommerce">WooCommerce</MenuItem>
            </TextField>
            <TextField size="small" label="Name" value={connectionForm.name} onChange={handleFieldChange("name")} fullWidth />
            <TextField size="small" label="Store URL" value={connectionForm.baseUrl} onChange={handleFieldChange("baseUrl")} fullWidth />
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
            <TextField size="small" label="Consumer key" value={connectionForm.consumerKey} onChange={handleFieldChange("consumerKey")} fullWidth />
            <TextField size="small" label="Consumer secret" type="password" value={connectionForm.consumerSecret} onChange={handleFieldChange("consumerSecret")} fullWidth />
            <Button variant="outlined" onClick={saveConnection} disabled={busy || !canUseImport || !connectionForm.baseUrl}>
              Save
            </Button>
          </Stack>

          {connections.length > 0 && (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="body2" color="text.secondary">Connection:</Typography>
              {connections.slice(0, 3).map((connection) => (
                <Chip
                  key={connection.id}
                  label={`${connection.name} (${connection.status})`}
                  color={String(connection.id) === String(connectionForm.id) ? "primary" : "default"}
                  onClick={() => setConnectionForm((prev) => ({
                    ...prev,
                    id: connection.id,
                    provider: connection.provider,
                    name: connection.name,
                    baseUrl: connection.baseUrl,
                    consumerKey: "",
                    consumerSecret: "",
                  }))}
                />
              ))}
            </Stack>
          )}

          <Divider />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between">
            <Box>
              <Typography variant="subtitle2">Product matching workspace</Typography>
              <Typography variant="body2" color="text.secondary">
                Scan products, review suggestions, then map each commerce product to an Elite Core product or explicit custom fallback.
              </Typography>
            </Box>
            <Button variant="contained" color="error" startIcon={<CloudDownloadIcon />} onClick={scanProducts} disabled={busy || !connectionForm.id}>
              Scan Products
            </Button>
          </Stack>

          {products.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip size="small" label={`${products.length} scanned`} />
              <Chip size="small" label={`${statusCounts.unmapped || 0} needs match`} color={statusCounts.unmapped ? "warning" : "default"} />
              <Chip size="small" label={`${statusCounts.suggested || 0} suggested`} color={statusCounts.suggested ? "info" : "default"} />
              <Chip size="small" label={`${statusCounts.mapped || 0} mapped`} color={statusCounts.mapped ? "success" : "default"} />
              <Chip size="small" label={`${statusCounts.imported || 0} imported`} color="success" />
              <Chip size="small" label={`${statusCounts.ignored || 0} ignored`} />
              <Chip size="small" label={`${statusCounts.failed || 0} failed`} color={statusCounts.failed ? "error" : "default"} />
            </Stack>
          )}

          {products.length > COMMERCE_IMPORT_PRODUCT_PAGE_SIZE && (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Showing {productPagination.startIndex + 1}-{productPagination.endIndex} of {productPagination.total} scanned products · Page {productPagination.currentPage + 1} of {productPagination.pageCount}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setProductPage((page) => Math.max(page - 1, 0))}
                  disabled={productPagination.currentPage === 0}
                >
                  Previous
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setProductPage((page) => Math.min(page + 1, productPagination.pageCount - 1))}
                  disabled={productPagination.currentPage >= productPagination.pageCount - 1}
                >
                  Next
                </Button>
              </Stack>
            </Stack>
          )}

          <Stack spacing={1.5}>
            {visibleProducts.map((product) => {
              const snapshot = product.externalSnapshot || {};
              const suggested = getSuggestedMatch(product);
              const selected = getSelectedMatch(product);
              const variants = Array.isArray(snapshot.variants) ? snapshot.variants : [];
              const imageUrl = imageFromProduct(snapshot);
              const categoryText = snapshot.categories?.map((category) => category.name).filter(Boolean).join(", ") || "No categories";
              const colorSize = summarizeColorsSizes(snapshot);

              return (
                <Paper key={product.id} variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} md={5}>
                      <Stack direction="row" spacing={1.25} alignItems="flex-start">
                        <Avatar variant="rounded" src={imageUrl || undefined} sx={{ width: 64, height: 64 }}>
                          W
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                            <Typography variant="subtitle2" fontWeight={800}>{snapshot.name || product.externalProductId}</Typography>
                            <Chip size="small" label={product.mappingStatus} />
                          </Stack>
                          <Typography variant="body2" color="text.secondary">SKU: {snapshot.sku || "No SKU"}</Typography>
                          <Typography variant="body2" color="text.secondary">Price: {money(snapshot.retail)}</Typography>
                          <Typography variant="body2" color="text.secondary">{variants.length || 1} variant{variants.length === 1 ? "" : "s"}</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">{categoryText}</Typography>
                          {colorSize && <Typography variant="caption" color="text.secondary" display="block">{colorSize}</Typography>}
                        </Box>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={7}>
                      <Stack spacing={1}>
                        {product.mappingStatus === "suggested" && suggested ? (
                          <Alert severity="info" sx={{ py: 0.5 }}>
                            Suggested match: <strong>{suggested.product?.name || suggested.productId}</strong> ({suggested.reason}, score {suggested.score})
                          </Alert>
                        ) : null}

                        {product.mappingStatus === "mapped" || product.mappingStatus === "imported" ? (
                          <Alert severity="success" sx={{ py: 0.5 }}>
                            Matched to {selected?.metadata?.name || selected?.productId || product.matchedProductId || "custom product"} ({selected?.sourceType || product.matchedSourceType})
                          </Alert>
                        ) : product.mappingStatus === "ignored" ? (
                          <Alert severity="warning" sx={{ py: 0.5 }}>Ignored for this import.</Alert>
                        ) : null}

                        <ProductSearchPanel
                          product={product}
                          importRecord={importRecord}
                          promoStoreId={promoStoreId}
                          instanceUniqueId={instanceUniqueId}
                          disabled={busy || product.mappingStatus === "imported"}
                          onSelect={(result) => saveMapping(product, {
                            sourceType: result.sourceType,
                            productId: result.productId,
                            metadata: {
                              name: result.name,
                              sku: result.sku,
                              supplierName: result.supplierName,
                              sourceLabel: result.sourceLabel,
                              imageUrl: result.imageUrl,
                              raw: result.raw || null,
                              variants: result.raw?.Variants || result.raw?.variants || [],
                            },
                          })}
                        />

                        <VariantMappingPanel
                          product={product}
                          selected={selected}
                          disabled={busy || product.mappingStatus === "imported"}
                          onSave={saveMapping}
                        />

                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          {suggested && product.mappingStatus === "suggested" && (
                            <Button
                              size="small"
                              variant="contained"
                              color="error"
                              startIcon={<CheckCircleOutlineIcon />}
                              onClick={() => saveMapping(product, {
                                sourceType: suggested.sourceType,
                                productId: suggested.productId,
                                metadata: suggested.product,
                              })}
                              disabled={busy}
                            >
                              Accept suggestion
                            </Button>
                          )}
                          <Tooltip title="Import this commerce product as an explicit custom product fallback">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => saveMapping(product, { sourceType: "external-custom" })}
                              disabled={busy || product.mappingStatus === "imported"}
                            >
                              Use as custom product
                            </Button>
                          </Tooltip>
                          <Button
                            size="small"
                            variant="text"
                            startIcon={<LinkOffIcon />}
                            onClick={() => saveMapping(product, { clear: true })}
                            disabled={busy || product.mappingStatus === "imported"}
                          >
                            Clear
                          </Button>
                          {product.mappingStatus === "ignored" ? (
                            <Button size="small" onClick={() => saveMapping(product, { clear: true })} disabled={busy}>Restore</Button>
                          ) : (
                            <Button
                              size="small"
                              color="inherit"
                              startIcon={<DoNotDisturbAltIcon />}
                              onClick={() => saveMapping(product, { ignore: true })}
                              disabled={busy || product.mappingStatus === "imported"}
                            >
                              Ignore
                            </Button>
                          )}
                        </Stack>
                      </Stack>
                    </Grid>
                  </Grid>
                </Paper>
              );
            })}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Close</Button>
        <Button
          variant="contained"
          color="error"
          onClick={importProducts}
          disabled={busy || !importRecord?.id || importableProducts.length === 0}
        >
          Import Mapped
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PromoStoreCommerceImportDialog;
